/*eslint  @typescript-eslint/no-use-before-define: 0*/

import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "@/store";
import { CriterionBase } from "@/models/api/filters/criterions/CriterionBase";
import { DataOptions } from "vuetify";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import _ from "lodash";
import { FILTER_NAMES } from "@/static/filterNames";
import { getServiceType, getServiceTypesWithLocked } from "@/requests/serviceType";
import { ServiceTypeListItem, ServiceTypesResponse } from "@/models/api/serviceTypes/ServiceTypeListItem";
import {
  addServiceType,
  addServiceTypeAttributeTemplate,
  editServiceType,
  editServiceTypeAttributeTemplate,
  getAssignedCommissionServiceType,
  getServiceTypeAttributeTemplate,
  removeServiceType,
  removeServiceTypeAttributeTemplate,
  slideDownServiceTypeAttributeTemplate,
  slideUpServiceTypeAttributeTemplate,
  getFeePublicProcurementXmlDescriptions,
  getDiscountPublicProcurementXmlDescriptions,
  getServiceTypesToCopyFrom,
  postCopyServiceTypeSettings,
  getExemptionReasonForZWRate,
} from "@/requests/administration/servicesAndAttributes/administrationServiceTypes";
import { ServiceTypeAttributeTemplate } from "@/models/api/administration/servicesAndAttributes/ServiceTypeAttributeTemplate";
import { ServiceTypeAttributeTemplateEdit } from "@/models/api/administration/servicesAndAttributes/ServiceTypeAttributeTemplateEdit";
import { ServiceTypeFull } from "@/models/api/serviceTypes/ServiceTypeFull";
import AdministrationServiceTypeExportConfigurationModule from "@/store/modules/administration/administrationServiceTypeExportConfiguration";
import { ConfirmModel } from "@/models/components/popup/Confirm";
import { filterCapitalize } from "@/filters/capitalize";
import i18n from "@/plugins/i18n";
import { SelectItem } from "@/models/api/SelectItem";
import {
  CopyServiceTypeSettings,
  ServiceTypeToCopyFrom,
} from "@/models/api/administration/servicesAndAttributes/ServiceTypeToCopyFrom";
import { SERVICE_CATEGORY } from "@/static/serviceCategory";

export interface AdministrationServiceTypesStateInterface {
  filters: null | Array<CriterionBase>;
  dataTableOptions: DataOptions;
  serviceTypes: Array<ServiceTypeListItem>;
  selectedServiceTypeId: null | number;
  countItems: number | null;
  serviceAttributeTemplates: Array<ServiceTypeAttributeTemplate>;
  serviceTypeAttributeTemplateEditId: number | null;
  serviceTypeAttributeTemplateNamePreview: string;
  serviceTypeAttributeTemplateEditModel: ServiceTypeAttributeTemplateEdit;
  isAddEditDialog: boolean;
  serviceTypeAddEditId: null | number;
  serviceTypeAddEdit: ServiceTypeFull;
  changesCountServiceTypeAttributeTemplate: number;
  feePublicProcurementXmlDescriptions: null | Array<SelectItem>;
  discountPublicProcurementXmlDescriptions: null | Array<SelectItem>;
  exemptionReasonZWRate: null | Array<SelectItem>;
  serviceTypeToCopyTo: ServiceTypeListItem | null;
  serviceTypesToCopyFrom: Array<ServiceTypeToCopyFrom>;
}

export const AdministrationServiceTypesState: AdministrationServiceTypesStateInterface = {
  filters: null,
  dataTableOptions: new DefaultDataOptions(),
  serviceTypes: [],
  selectedServiceTypeId: null,
  countItems: null,
  serviceAttributeTemplates: [],
  serviceTypeAttributeTemplateEditId: null, // isEditServiceTypeAttributeTemplateDialog: false,
  serviceTypeAttributeTemplateNamePreview: "",
  serviceTypeAttributeTemplateEditModel: new ServiceTypeAttributeTemplateEdit(),
  isAddEditDialog: false,
  serviceTypeAddEditId: null,
  serviceTypeAddEdit: new ServiceTypeFull(),
  changesCountServiceTypeAttributeTemplate: 0,
  feePublicProcurementXmlDescriptions: null,
  discountPublicProcurementXmlDescriptions: null,
  exemptionReasonZWRate: null,
  serviceTypeToCopyTo: null,
  serviceTypesToCopyFrom: [],
};

const AdministrationServiceTypesModule = defineModule({
  modules: { exportConfiguration: AdministrationServiceTypeExportConfigurationModule },
  state: AdministrationServiceTypesState as AdministrationServiceTypesStateInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    setFilters(state, param: null | Array<CriterionBase>) {
      state.filters = param;
    },
    setDataTableOptions(state, param: DataOptions) {
      state.dataTableOptions = param;
    },
    setServiceTypes(state, param: ServiceTypesResponse) {
      state.serviceTypes = param.items;
      state.countItems = param.totalCount;
    },
    setSelectedServiceTypeId(state, param: number | null) {
      state.selectedServiceTypeId = param;
    },
    setServiceAttributeTemplates(state, param: Array<ServiceTypeAttributeTemplate>) {
      state.serviceAttributeTemplates = param;
    },
    setServiceTypeAttributeTemplateEditId(state, param: number | null) {
      state.serviceTypeAttributeTemplateEditId = param;
    },
    setServiceTypeAttributeTemplateNamePreview(state, param: string) {
      state.serviceTypeAttributeTemplateNamePreview = param;
    },
    setServiceTypeAttributeTemplateEditModel(state, param: ServiceTypeAttributeTemplateEdit) {
      state.serviceTypeAttributeTemplateEditModel = param;
    },
    setIsAddEditDialog(state, param: boolean) {
      state.isAddEditDialog = param;
    },
    setServiceTypeAddEditId(state, param: null | number) {
      state.serviceTypeAddEditId = param;
    },
    setServiceTypeAddEdit(state, param: ServiceTypeFull) {
      state.serviceTypeAddEdit = param;
    },
    riseChangesCountServiceTypeAttributeTemplate(state) {
      state.changesCountServiceTypeAttributeTemplate++;
    },
    setFeePublicProcurementXmlDescriptions(state, param: Array<SelectItem>) {
      state.feePublicProcurementXmlDescriptions = param;
    },
    setDiscountPublicProcurementXmlDescriptions(state, param: Array<SelectItem>) {
      state.discountPublicProcurementXmlDescriptions = param;
    },
    setExemptionReasonZWRate(state, param: Array<SelectItem>) {
      state.exemptionReasonZWRate = param;
    },
    setServiceTypesToCopyFrom(state, param: Array<ServiceTypeToCopyFrom>) {
      state.serviceTypesToCopyFrom = param;
    },
    setServiceTypeToCopyTo(state, param: ServiceTypeListItem | null) {
      state.serviceTypeToCopyTo = param;
    },
  },
  actions: {
    async downloadServiceTypes(context) {
      const { commit, state } = _moduleActionContext(context);
      commit.setSelectedServiceTypeId(null);
      const serviceTypesResponse = await getServiceTypesWithLocked(false, {
        count: state.dataTableOptions.itemsPerPage,
        page: state.dataTableOptions.page - 1,
        criterions: _.cloneDeep(state.filters),
        filterName: FILTER_NAMES.ServiceTypeListFilter,
      });
      commit.setServiceTypes(serviceTypesResponse);
      if (serviceTypesResponse.items.length) commit.setSelectedServiceTypeId(serviceTypesResponse.items[0].id);
    },
    async getFeePublicProcurementXmlDescriptions(context) {
      const { commit } = _moduleActionContext(context);
      const response = await getFeePublicProcurementXmlDescriptions();
      commit.setFeePublicProcurementXmlDescriptions(response);
    },
    async getDiscountPublicProcurementXmlDescriptions(context) {
      const { commit } = _moduleActionContext(context);
      const response = await getDiscountPublicProcurementXmlDescriptions();
      commit.setDiscountPublicProcurementXmlDescriptions(response);
    },
    async getExemptionReasonForZWRate(context) {
      const { commit } = _moduleActionContext(context);
      const response = await getExemptionReasonForZWRate();
      commit.setExemptionReasonZWRate(response);
    },
    async downloadServiceAttributeTemplates(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.selectedServiceTypeId !== null) {
        const serviceAttributeTemplates = await getServiceTypeAttributeTemplate(state.selectedServiceTypeId);
        commit.setServiceAttributeTemplates(serviceAttributeTemplates);
      }
    },
    async slideUpAttribute(context, serviceTypeAttributeTemplateId: number) {
      const { state, rootDispatch, commit } = _moduleActionContext(context);
      if (state.selectedServiceTypeId !== null) {
        await rootDispatch.failIfRequestsInProgress();
        await slideUpServiceTypeAttributeTemplate(state.selectedServiceTypeId, serviceTypeAttributeTemplateId);
        commit.riseChangesCountServiceTypeAttributeTemplate();
      }
    },
    async slideDownAttribute(context, serviceTypeAttributeTemplateId: number) {
      const { commit, state, rootDispatch } = _moduleActionContext(context);
      if (state.selectedServiceTypeId !== null) {
        await rootDispatch.failIfRequestsInProgress();
        await slideDownServiceTypeAttributeTemplate(state.selectedServiceTypeId, serviceTypeAttributeTemplateId);
        commit.riseChangesCountServiceTypeAttributeTemplate();
      }
    },
    async removeAttributeFromService(context, attributeTemplateId: number) {
      const { commit, state, rootDispatch } = _moduleActionContext(context);
      if (state.selectedServiceTypeId !== null) {
        await rootDispatch.failIfRequestsInProgress();
        await removeServiceTypeAttributeTemplate([state.selectedServiceTypeId], [attributeTemplateId]);
        commit.riseChangesCountServiceTypeAttributeTemplate();
      }
    },
    async addAttributeToService(context, attributeTemplateId: number) {
      const { commit, state, rootDispatch } = _moduleActionContext(context);
      if (state.selectedServiceTypeId !== null) {
        await rootDispatch.failIfRequestsInProgress();
        await addServiceTypeAttributeTemplate([state.selectedServiceTypeId], [attributeTemplateId]);
        commit.riseChangesCountServiceTypeAttributeTemplate();
      }
    },
    async openEditServiceTypeAttributeTemplateDialog(
      context,
      serviceTypeAttributeTemplate: ServiceTypeAttributeTemplate
    ) {
      const { commit, state } = _moduleActionContext(context);
      const model = state.serviceTypeAttributeTemplateEditModel;
      model.isPrintable = serviceTypeAttributeTemplate.isPrintable;
      model.defaultValue = serviceTypeAttributeTemplate.defaultValue;
      model.isRequired = serviceTypeAttributeTemplate.isRequired;
      commit.setServiceTypeAttributeTemplateEditModel(model);
      commit.setServiceTypeAttributeTemplateNamePreview(serviceTypeAttributeTemplate.attributeTemplate.name);
      commit.setServiceTypeAttributeTemplateEditId(serviceTypeAttributeTemplate.id);
    },
    async saveEditServiceTypeAttributeTemplate(context) {
      const { rootDispatch, state, commit } = _moduleActionContext(context);
      if (state.serviceTypeAttributeTemplateEditId !== null) {
        await rootDispatch.failIfRequestsInProgress();
        await editServiceTypeAttributeTemplate(
          state.serviceTypeAttributeTemplateEditId,
          state.serviceTypeAttributeTemplateEditModel
        );
        commit.setServiceTypeAttributeTemplateEditId(null);
        rootDispatch.popup.addOperationSuccessfulSnackbar();
        commit.riseChangesCountServiceTypeAttributeTemplate();
      }
    },
    async openNewServiceTypeDialog(context) {
      const { commit } = _moduleActionContext(context);
      commit.setServiceTypeAddEdit(new ServiceTypeFull());
      commit.setServiceTypeAddEditId(null);
      commit.setIsAddEditDialog(true);
    },
    async openEditServiceTypeDialog(context, serviceTypeId: number) {
      const { commit } = _moduleActionContext(context);
      commit.setServiceTypeAddEdit(await getServiceType(serviceTypeId));
      commit.setServiceTypeAddEditId(serviceTypeId);
      commit.setIsAddEditDialog(true);
    },
    async confirmServiceLockIfIsAssignedToServices(context) {
      const { state, rootDispatch } = _moduleActionContext(context);
      if (state.serviceTypeAddEditId !== null) {
        const commissionToServiceTypes = await getAssignedCommissionServiceType(state.serviceTypeAddEditId);
        if (commissionToServiceTypes.length > 0) {
          let message = filterCapitalize(i18n.t("thisServiceIsAssignedToServices"));
          for (const commissionToServiceType of commissionToServiceTypes) {
            message += "<li>" + commissionToServiceType + "</li>";
          }
          message += "<br/>" + filterCapitalize(i18n.t("doYouReallyWantToLockService?"));
          if (
            !(await rootDispatch.popup.addConfirm(
              new ConfirmModel(
                message,
                filterCapitalize(i18n.t("warning")),
                [
                  { text: filterCapitalize(i18n.t("cancel")), color: "warning", value: false, outlined: true },
                  { text: filterCapitalize(i18n.t("confirm")), color: "warning", value: true },
                ],
                "450px"
              )
            ))
          ) {
            throw new Error("User cancelled action");
          }
        }
      }
    },
    async saveServiceType(context) {
      const { commit, state, rootDispatch, dispatch, rootCommit } = _moduleActionContext(context);
      if (state.serviceTypeAddEditId !== null) {
        await rootDispatch.failIfRequestsInProgress();
        if (state.serviceTypeAddEdit.isLocked) {
          await dispatch.confirmServiceLockIfIsAssignedToServices();
        }
        await editServiceType(state.serviceTypeAddEdit, state.serviceTypeAddEditId);
      } else {
        await addServiceType(state.serviceTypeAddEdit);
      }
      commit.setIsAddEditDialog(false);
      rootDispatch.popup.addOperationSuccessfulSnackbar();
      await dispatch.downloadServiceTypes();
      rootCommit.serviceTypes.clearCache();
      rootDispatch.serviceTypes.downloadServiceTypes(SERVICE_CATEGORY.Sale);
    },
    async removeServiceType(context, serviceTypeId: number) {
      const { rootDispatch, dispatch, rootCommit } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      if (await rootDispatch.popup.addConfirmRemoveElement()) {
        await removeServiceType(serviceTypeId);
        rootDispatch.popup.addOperationSuccessfulSnackbar();
        await dispatch.downloadServiceTypes();
        rootCommit.serviceTypes.clearCache();
        rootDispatch.serviceTypes.downloadServiceTypes(SERVICE_CATEGORY.Sale);
      }
    },
    async downloadServiceTypesToCopyFrom(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.serviceTypeToCopyTo !== null) {
        const response = await getServiceTypesToCopyFrom(state.serviceTypeToCopyTo.id);
        commit.setServiceTypesToCopyFrom(response);
      }
    },
    async copyServiceTypeSettings(context, model: CopyServiceTypeSettings) {
      const { state } = _moduleActionContext(context);
      if (state.serviceTypeToCopyTo !== null) {
        await postCopyServiceTypeSettings(state.serviceTypeToCopyTo.id, model);
      }
    },
  },
});

export default AdministrationServiceTypesModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) => moduleGetterContext(args, AdministrationServiceTypesModule);
const _moduleActionContext = (context: any) => moduleActionContext(context, AdministrationServiceTypesModule);
